import { useCallback, useState } from 'react';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { type Section, type SharedSection } from '#shared/api/schema';
import { persistentStorage } from '#shared/lib/storage';
import { showSuccess } from '#shared/lib/toast';
import { constructorProductLoggingEvents } from '../lib/constructorProductLoggingEvents';
import { getConstructorPreviewPage, getConstructorSectionSettings } from '../model/slices';
import { type ConstructorRightBarSectionType } from '../model/slices/types';

export type CopiedSection = {
    sectionInstanceId: string;
    moduleId?: string;
    sectionSettings: SharedSection & {
        display_sequence: number;
        id: string;
        section_type: Section['section_type'];
    };
    widget: string;
    copyDate: number;
};

const tenMinutesMs = 10 * 60 * 1000;

export const useConstructorCopiedSection = () => {
    const [copiedSection, setCopiedSection] = useState<CopiedSection | null>(null);

    const copySection = useCallback(
        (sectionToCopy: ConstructorRightBarSectionType) => {
            if (!sectionToCopy) {
                return;
            }

            const pageId = getConstructorPreviewPage();
            const widget = getConstructorSectionSettings(pageId, sectionToCopy.sectionInstanceId)?.widget ?? '';
            const sectionSettings = getConstructorSectionSettings(
                pageId,
                sectionToCopy.sectionInstanceId,
                sectionToCopy.moduleId,
                true
            );

            if (!sectionSettings) {
                return;
            }

            const copyItem: CopiedSection = {
                sectionInstanceId: sectionToCopy.sectionInstanceId,
                moduleId: sectionToCopy.moduleId,
                widget,
                sectionSettings,
                copyDate: Date.now(),
            };

            setCopiedSection(copyItem);
            persistentStorage.setItem('constructor.copiedSection', JSON.stringify(copyItem));
            logEventString(constructorProductLoggingEvents.Section.Copy, 'true', {
                widget,
                hasModule: Boolean(sectionToCopy.moduleId),
            });
            showSuccess(sectionToCopy.moduleId ? 'Элемент скопирован' : 'Секция скопирована');
        },
        [setCopiedSection]
    );

    const checkCopiedSection = useCallback(() => {
        const copiedData = persistentStorage.getItem('constructor.copiedSection');

        if (!copiedData) {
            setCopiedSection(null);

            return;
        }

        const copiedSection: CopiedSection = JSON.parse(copiedData);

        if (copiedSection && copiedSection.copyDate && Date.now() - copiedSection.copyDate < tenMinutesMs) {
            setCopiedSection(copiedSection);
        } else {
            persistentStorage.removeItem('constructor.copiedSection');
            setCopiedSection(null);
        }
    }, [setCopiedSection]);

    return {
        copiedSection,
        copySection,
        checkCopiedSection,
    };
};
